"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { withSlotScopeClasses } from '../../../helpers/scoped-slots.mjs';
import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { EditorTagNode } from './nodes/EditorTagNode.mjs';
import { ConfigPlugin } from './plugins/ConfigPlugin.mjs';
import { EditorTagPlugin } from './plugins/EditorTagPlugin.mjs';
import { KeyboardPlugin } from './plugins/KeyboardPlugin.mjs';
import { StateSyncPlugin } from './plugins/StateSyncPlugin.mjs';

const initialConfig = {
  namespace: "data-input-editor",
  onError: console.error,
  nodes: [EditorTagNode]
};
function LexicalProvider({ children }) {
  return /* @__PURE__ */ jsx(LexicalComposer, { initialConfig, children });
}
const PLACEHOLDER_CSS_VARIABLE = "--placeholder-text";
const editorTagBadgeRecipe = tv({
  slots: withSlotScopeClasses("data-input-editor", {
    root: [
      "text-xs font-medium min-w-12",
      "focus:outline-none",
      "[&_[data-lexical-text='true']]:text-slate-12",
      "[[data-field-redacted='true']_&_[data-lexical-text='true']]:filter",
      "[[data-field-redacted='true']_&_[data-lexical-text='true']]:blur-[3px]",
      "[&_p]:my-1 [&_p]:py-1 [&_p]:px-1 [&_p]:rounded-md [&_p]:min-h-5",
      "[&_p:has([data-editor-tag='true'])]:py-0",
      "[&_p]:before:content-[var(--placeholder-text)] [&_p]:before:float-left [&_p]:before:pointer-events-none [&_p]:before:h-0 [&_p]:before:text-gray-9",
      "[&_p:not(:has(>br:only-child))]:before:content-none",
      "[&_p>*]:align-middle [&_p>*]:inline-block [&_p>*]:max-w-full"
    ]
  })
});
const styles = editorTagBadgeRecipe();
const LexicalEditor = forwardRef(({ onChange, value, schema, placeholder, disabled, ...props }, ref) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      PlainTextPlugin,
      {
        contentEditable: /* @__PURE__ */ jsx(
          ContentEditable,
          {
            ref,
            spellCheck: false,
            autoCorrect: "off",
            autoCapitalize: "off",
            autoComplete: "off",
            ...props,
            "data-testid": "data-input-editor",
            "data-editor-container": "true",
            "data-placeholder": placeholder,
            "data-gramm": "false",
            "data-gramm_editor": "false",
            "data-enable-grammarly": "false",
            "data-lt-active": "false",
            className: styles.root({ className: props.className }),
            style: {
              [PLACEHOLDER_CSS_VARIABLE]: `"${placeholder}"`
            }
          }
        ),
        ErrorBoundary: LexicalErrorBoundary
      }
    ),
    /* @__PURE__ */ jsx(HistoryPlugin, {}),
    /* @__PURE__ */ jsx(EditorTagPlugin, {}),
    /* @__PURE__ */ jsx(KeyboardPlugin, {}),
    /* @__PURE__ */ jsx(OnChangePlugin, { ignoreSelectionChange: true, ignoreHistoryMergeTagChange: true, onChange }),
    /* @__PURE__ */ jsx(StateSyncPlugin, { value, schema }),
    /* @__PURE__ */ jsx(ConfigPlugin, { disabled })
  ] });
});
LexicalEditor.displayName = "LexicalEditor";

export { LexicalEditor, LexicalProvider };
